import { type AuthUser, type PermissionCode, scopeCovers } from '@erp/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from './api';

export const meQueryKey = ['auth', 'me'] as const;

export function fetchMe() {
  return api<AuthUser>('/auth/me');
}

/** Текущий пользователь. `null` — не вошёл. */
export function useMe() {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: async () => {
      try {
        return await fetchMe();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

export function useUser(): AuthUser {
  const { data } = useMe();
  if (!data) throw new Error('useUser вне защищённой части приложения');
  return data;
}

/**
 * Проверка права в интерфейсе — только чтобы прятать недоступные кнопки.
 * Настоящая проверка всегда на сервере.
 */
export function useCan() {
  const user = useUser();
  return (code: PermissionCode, departmentPath?: string | null) => {
    const scope = user.permissions[code];
    if (departmentPath === undefined) return Boolean(scope);
    return scopeCovers(scope, departmentPath);
  };
}

export function useLogout() {
  const qc = useQueryClient();
  return async () => {
    await api('/auth/logout', { method: 'POST' }).catch(() => {});
    qc.clear();
    window.location.assign('/login');
  };
}
